import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { HeadlineOverlay } from '@/store/videoFeatures/types/headline-overlay';

interface HeadlineAboveProps {
  headline: HeadlineOverlay;
}

export const HeadlineAbove: React.FC<HeadlineAboveProps> = ({ headline }) => {
  useLanguage();
  if (!headline?.enabled) return null;

  // Converte o `size` salvo no state (number em pixels) para a string CSS
  // correspondente. Aceita number direto ou string numérica/enum legado por
  // segurança (dados cacheados antes da migration para INT).
  const LEGACY_SIZE_MAP: Record<string, number> = {
    small: 16, medium: 20, large: 28, xlarge: 36,
    pequeno: 16, medio: 20, grande: 28,
  };
  const getFontSize = (raw: unknown, defaultPx = 20): string => {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return `${raw}px`;
    if (typeof raw === 'string') {
      const num = parseInt(raw, 10);
      if (!isNaN(num) && num > 0) return `${num}px`;
      const legacy = LEGACY_SIZE_MAP[raw.trim().toLowerCase()];
      if (legacy) return `${legacy}px`;
    }
    return `${defaultPx}px`;
  };

  // Get font weight
  const getFontWeight = (weight?: string) => {
    if (weight === 'bold') return '700';
    return '400'; // normal
  };

  // Image mode - SEM fallback (desktop e mobile independentes)
  if (headline.imageMode?.enabled) {
    const isMobile = window.innerWidth < 768;
    const imageUrl = isMobile
      ? headline.imageMode.mobileImage
      : headline.imageMode.desktopImage;

    // Se não tem imagem para o dispositivo atual, não renderiza nada
    if (!imageUrl) return null;

    return (
      <div className="w-full flex justify-center mb-4">
        <img
          src={imageUrl}
          alt={translateInterfaceText("Headline")}
          className="max-w-[90%]"
          style={{ maxHeight: '120px' }}
        />
      </div>
    );
  }

  // HTML mode
  if (headline.htmlMode?.enabled && headline.htmlMode.code) {
    return (
      <div
        className="w-full flex justify-center mb-4 text-center"
        style={{ maxWidth: '90%', margin: '0 auto 1rem auto' }}
        dangerouslySetInnerHTML={{ __html: headline.htmlMode.code }}
      />
    );
  }

  // Text mode (only render if explicitly enabled or by default)
  const isTextMode = headline.textMode?.enabled ?? (!headline.htmlMode.enabled && !headline.imageMode.enabled);
  if (!isTextMode) return null;

  const hasBackground = headline.background?.enabled;
  const backgroundColor = hasBackground ?
    headline.background?.color || 'rgba(0, 0, 0, 0.7)' :
    'transparent';

  return (
    <div
      className="w-full flex justify-center mb-4"
      style={{
        padding: hasBackground ? '8px 12px' : '0',
        borderRadius: hasBackground ? '4px' : '0',
      }}
    >
      <div className="text-center" style={{ maxWidth: '90%' }}>
        {headline.headline?.text && (
          <p
            ref={(el) => {
              if (el) {
                el.style.setProperty('font-size', getFontSize(headline.headline.size, 24), 'important');
              }
            }}
            style={{
              color: headline.headline.color,
              fontFamily: headline.headline.fontFamily,
              fontWeight: getFontWeight(headline.headline.fontWeight),
              margin: 0,
            }}
          >
            {headline.headline.text}
          </p>
        )}

        {headline.subheadline?.text && (
          <p
            ref={(el) => {
              if (el) {
                el.style.setProperty('font-size', getFontSize(headline.subheadline.size, 16), 'important');
              }
            }}
            style={{
              color: headline.subheadline.color,
              fontFamily: headline.subheadline.fontFamily,
              fontWeight: getFontWeight(headline.subheadline.fontWeight),
              margin: '4px 0 0 0',
            }}
          >
            {headline.subheadline.text}
          </p>
        )}
      </div>
    </div>
  );
};
